/**
 * Shared types for auto-updates feature (renderer ↔ Electron).
 *
 * These types define the contract between the main process (Electron)
 * and the renderer process (React) for update state management.
 */

/** Current status of the auto-update system */
export type AutoUpdateStatus =
  | 'idle'
  | 'checking'
  | 'available'
  | 'downloading'
  | 'downloaded'
  | 'up-to-date'
  | 'error'
  | 'disabled';

/** Update info returned when an update is available */
export interface UpdateInfo {
  /** Version string (semver) */
  version: string;
  /** Release date (ISO string) */
  releaseDate?: string;
  /** Release notes (markdown) */
  releaseNotes?: string;
}

/** Download progress information */
export interface UpdateDownloadProgress {
  /** Bytes transferred so far */
  bytesTransferred: number;
  /** Total bytes to download */
  totalBytes: number;
  /** Download percentage (0-100) */
  percent: number;
}

/**
 * macOS situations known to break update installs, detected when a failed
 * install is discovered (attached to telemetry + shown as a hint in the
 * manual-download fallback UI).
 */
export type UpdateInstallIssue = 'read-only-location' | 'not-in-applications';

/** Current state of the auto-update system */
export interface AutoUpdateState {
  /** Current status */
  status: AutoUpdateStatus;
  /** Update info when available/downloading/downloaded */
  updateInfo: UpdateInfo | null;
  /** Download progress when downloading */
  downloadProgress: UpdateDownloadProgress | null;
  /** Error message when status is 'error' */
  errorMessage: string | null;
  /** Current app version */
  currentVersion: string | null;
  /** Current architecture (arm64, x64, etc.) */
  arch: string | null;
  /** Whether auto-updates are disabled via env var */
  isDisabled: boolean;
  /** Timestamp of last check (ISO string) */
  lastCheckAt: string | null;
  /**
   * True when an update is known available but auto-update has failed
   * repeatedly (>= threshold consecutive times). The renderer shows a quiet,
   * dismissible "download manually" affordance in this state.
   */
  updateFallbackActive: boolean;
  /**
   * True when a previous session downloaded an update but the app has since
   * started >= threshold times still on the old version — install-on-quit is
   * silently failing on this device. Detected once at startup (never changes
   * mid-session); the renderer surfaces the manual-download fallback.
   */
  installFailureDetected: boolean;
  /** Detected macOS-specific reason installs may be failing, if any. */
  macInstallIssue: UpdateInstallIssue | null;
}

/** Result of a manual check for updates */
export interface CheckForUpdatesResult {
  success: boolean;
  /** New state after check */
  state: AutoUpdateState;
  /** Error message if check failed */
  error?: string;
}

/** Result of install/restart action */
export interface InstallUpdateResult {
  success: boolean;
  error?: string;
}

/** Events emitted from main process to renderer */
export type AutoUpdateEvent =
  | { type: 'checking' }
  | { type: 'update-available'; info: UpdateInfo }
  | { type: 'update-not-available' }
  | { type: 'download-progress'; progress: UpdateDownloadProgress }
  | { type: 'update-downloaded'; info: UpdateInfo }
  | { type: 'error'; message: string }
  | { type: 'update-fallback'; active: boolean; info: UpdateInfo | null };

/** Initial state factory */
export function createInitialAutoUpdateState(): AutoUpdateState {
  return {
    status: 'idle',
    updateInfo: null,
    downloadProgress: null,
    errorMessage: null,
    currentVersion: null,
    arch: null,
    isDisabled: false,
    lastCheckAt: null,
    updateFallbackActive: false,
    installFailureDetected: false,
    macInstallIssue: null,
  };
}

/**
 * Type guard to check if updates are actionable (can check/download).
 * Returns false when disabled or already in a transitional state.
 */
export function canCheckForUpdates(state: AutoUpdateState): boolean {
  if (state.isDisabled) return false;
  // Don't check if already checking or downloading
  return state.status !== 'checking' && state.status !== 'downloading';
}

/**
 * Type guard to check if an update is ready to install.
 */
export function canInstallUpdate(state: AutoUpdateState): boolean {
  return state.status === 'downloaded' && state.updateInfo !== null;
}

// =============================================================================
// Mock Adapter Types (for E2E testing)
// =============================================================================

/** Mock scenario configuration for testing */
export type MockUpdateScenario = {
  /** Type of scenario to simulate */
  type: 'no-update' | 'update-available' | 'error';
  /** Update info when type is 'update-available' */
  updateInfo?: UpdateInfo;
  /** Error message when type is 'error' */
  errorMessage?: string;
  /** Delay in ms before emitting events (default: 100) */
  delay?: number;
};

/** Mock adapter internal state (for assertions in tests) */
export type MockAdapterState = {
  scenario: MockUpdateScenario;
  downloadedUpdateInfo: UpdateInfo | null;
  isDownloading: boolean;
};

/** Scheduler state (for E2E testing assertions) */
export type SchedulerState = {
  /** Whether the scheduler is running */
  isRunning: boolean;
  /** Whether a check is currently in flight */
  isChecking: boolean;
  /** Timestamp of last check (ms since epoch) */
  lastCheckAt: number | null;
  /** Timestamp when downloaded cooldown ends (ms since epoch) */
  cooldownEndsAt: number | null;
  /** Whether startup timeout is pending */
  startupTimeoutId: unknown | null;
  /** Whether interval is active */
  intervalId: unknown | null;
};
